import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import './timeline-drawer.css';

type DrawerSize = { minimum: number; maximum: number; base: number };
const clamp = (height: number, size: DrawerSize) => Math.max(size.minimum, Math.min(size.maximum, height));

/** The grid slot stays fixed while this panel rises over its neighboring panes. */
export function TimelineDrawer({ initialCollapsed = false, collapsed: requestedCollapsed, onCollapsedChange, layoutRevision, onReveal, onEdit, contentId = 'review-keyframe-area', children }: {
  contentId?: string; initialCollapsed?: boolean; collapsed?: boolean; onCollapsedChange?: (collapsed: boolean) => void; layoutRevision?: boolean; onReveal: () => void; onEdit?: () => void;
  children: (controls: { open: boolean; toggle: () => void }) => ReactNode;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const drag = useRef<{ pointer: number; y: number; height: number } | null>(null);
  const savedHeight = useRef<number | null>(null);
  const savedCollapsed = useRef(false);
  const [height, setHeight] = useState<number | null>(null);
  const [ownCollapsed, setOwnCollapsed] = useState(initialCollapsed);
  const collapsed = requestedCollapsed ?? ownCollapsed;
  const wasCollapsed = useRef(collapsed);
  const setCollapsed = (value: boolean) => { setOwnCollapsed(value); onCollapsedChange?.(value); };
  const [dragging, setDragging] = useState(false);
  const [size, setSize] = useState<DrawerSize>({ minimum: 180, maximum: 800, base: 204 });
  useLayoutEffect(() => {
    const opening = wasCollapsed.current && !collapsed;
    wasCollapsed.current = collapsed;
    if (opening && height === null && size.base < 180) setHeight(clamp(Math.max(320, size.maximum * .7), size));
  }, [collapsed, height, size]);
  useLayoutEffect(() => {
    const slot = panel.current?.parentElement, workspace = slot?.closest('[data-timeline-workspace]');
    if (!slot || !workspace) return;
    const measure = () => {
      const maximum = Math.max(72, slot.getBoundingClientRect().bottom - workspace.getBoundingClientRect().top);
      const base = slot.clientHeight;
      const next = { minimum: Math.min(180, maximum, base || 180), maximum, base };
      setSize(next); setHeight(current => current === null ? null : clamp(current, next));
    };
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(slot); observer?.observe(workspace);
    window.addEventListener('resize', measure);
    return () => { observer?.disconnect(); window.removeEventListener('resize', measure); };
  }, [layoutRevision]);
  useEffect(() => {
    if (!dragging) return;
    const move = (event: PointerEvent) => {
      const start = drag.current;
      if (start?.pointer === event.pointerId) {
        setHeight(clamp(start.height + start.y - event.clientY, size)); setCollapsed(false); onReveal();
      }
    };
    const stop = () => { drag.current = null; setDragging(false); };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', stop, true);
    window.addEventListener('pointercancel', stop, true);
    window.addEventListener('blur', stop);
    return () => {
      window.removeEventListener('pointermove', move, true); window.removeEventListener('pointerup', stop, true);
      window.removeEventListener('pointercancel', stop, true); window.removeEventListener('blur', stop);
    };
  }, [dragging, size, onReveal]);
  const reveal = () => {
    if (collapsed && height === null && size.base < 180) setHeight(clamp(Math.max(320, size.maximum * .7), size));
    setCollapsed(false); onReveal();
  };
  const toggle = () => { if (collapsed) reveal(); else setCollapsed(true); };
  const currentHeight = height ?? size.base;
  const expanded = !collapsed && currentHeight >= size.maximum - 2;
  const expand = () => {
    if (expanded) { setHeight(savedHeight.current); setCollapsed(savedCollapsed.current); }
    else {
      savedHeight.current = height; savedCollapsed.current = collapsed;
      setHeight(size.maximum); setCollapsed(false); onReveal();
    }
  };
  const adjust = (next: number) => { reveal(); setHeight(clamp(next, size)); };
  const style = { height: height === null ? '100%' : `${height}px`, maxHeight: `${size.maximum}px` } satisfies CSSProperties;
  return <div ref={panel} className="timeline-drawer" data-collapsed={collapsed} data-dragging={dragging} style={collapsed ? { maxHeight: style.maxHeight } : style}>
    {dragging && <div className="timeline-drag-shield" aria-hidden="true" />}
    <div className="timeline-drawer-bar">
      <div className="timeline-drawer-handle" role="separator" tabIndex={0} aria-label="Timeline height" aria-orientation="horizontal"
        aria-controls={contentId} aria-valuemin={0} aria-valuemax={Math.round(size.maximum)}
        aria-valuenow={collapsed ? 0 : Math.round(currentHeight)}
        aria-valuetext={collapsed ? 'Collapsed' : `${Math.round(currentHeight)} pixels`}
        title="Drag up for more keyframes. Arrow keys adjust height; Home restores, End expands. Double-click to expand or restore."
        onDoubleClick={expand} onKeyDown={event => {
          if (!['ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault(); event.stopPropagation();
          if (event.key === 'Home') { reveal(); setHeight(null); }
          else adjust(event.key === 'End' ? size.maximum : currentHeight + (event.key === 'ArrowUp' ? 1 : -1) * (event.shiftKey ? 80 : 24));
        }} onPointerDown={event => {
          if (event.button !== 0) return;
          event.preventDefault();
          event.currentTarget.focus(); window.getSelection()?.removeAllRanges();
          const startHeight = panel.current?.getBoundingClientRect().height ?? currentHeight;
          drag.current = { pointer: event.pointerId, y: event.clientY, height: startHeight };
          event.currentTarget.setPointerCapture(event.pointerId); setDragging(true);
        }} onPointerUp={event => {
          if (drag.current?.pointer !== event.pointerId) return;
          drag.current = null; setDragging(false);
          if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
        }} onPointerCancel={() => { drag.current = null; setDragging(false); }} onLostPointerCapture={() => { drag.current = null; setDragging(false); }}>
        <span className="timeline-grip" aria-hidden="true" /><span>Timeline <span className="timeline-drag-hint">· drag up</span></span>
      </div>
      {onEdit && <button type="button" className="timeline-drawer-edit" onClick={onEdit}>Edit</button>}
      <button type="button" onClick={expand} aria-expanded={expanded} aria-controls={contentId}>{expanded ? 'Restore' : 'Expand'}</button>
      <button type="button" onClick={toggle} aria-expanded={!collapsed} aria-controls={contentId}>{collapsed ? 'Show keys' : 'Collapse'}</button>
    </div>
    <div id={contentId} className="timeline-drawer-content">{children({ open: !collapsed, toggle })}</div>
  </div>;
}
